import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { getProfile, signOut } from 'src/store/app.state.store'
import { Tab, Tabs } from 'components/Utility/Tabs/Tabs'
import { UserInfo } from 'components/Profile/UserInfo/UserInfo'
import { Gifts } from 'components/Profile/Gifts/Gifts'
import { Cashback } from 'components/Profile/Cashback/Cashback'
import { ProfileEditForm } from 'components/Forms/ProfileEditForm/ProfileEditForm'
import { Visits } from 'components/Profile/Visits/Visits'
import styles from './UserProfile.module.scss'

type ProfileTab = 'visits' | 'cashback' | 'gifts' | 'personal'

/** Below this width the tabs scroll under a fixed indicator */
const NARROW_WIDTH = 428

const TAB_CONTENT: Record<ProfileTab, React.ComponentType> = {
  visits: Visits,
  gifts: Gifts,
  cashback: Cashback,
  personal: ProfileEditForm,
}

/** The personal account (#profile) */
export const UserProfile: React.FC = React.memo(() => {
  const [activeTab, setActiveTab] = useState<ProfileTab>('gifts')
  const [reverseTabs, setReverseTabs] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const profile = useSelector(getProfile)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setReverseTabs(containerRef.current.clientWidth <= NARROW_WIDTH)
      }
    }
    window.addEventListener('resize', handleResize, { passive: true })
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleSignOut = useCallback(() => {
    navigate('#')
    dispatch(signOut())
  }, [dispatch, navigate])

  if (!profile) {
    return null
  }

  const Content = TAB_CONTENT[activeTab]
  const tab = (value: ProfileTab, title: React.ReactNode) => (
    <Tab className={styles.tabContainer} selectedClassName={styles.selected} value={value}>
      <div className={styles.tab}>{title}</div>
    </Tab>
  )

  return (
    <div className={styles.container} ref={containerRef}>
      <UserInfo user={profile} />
      <Tabs
        reverseMove={reverseTabs}
        className={styles.tabsContainer}
        selected={activeTab}
        onChange={setActiveTab}
        tabsClassName={styles.tabs}
        indicatorClassName={styles.indicator}
      >
        {tab('visits', <>Мои записи {!!profile.appointmentsCount && <sup>{profile.appointmentsCount}</sup>}</>)}
        {tab('cashback', 'Мой кешбэк')}
        {tab('gifts', 'Мои подарки')}
        {tab('personal', 'Личные данные')}
        <div className={styles.tabContainer} onClick={handleSignOut}>
          <div className={styles.tab}>
            Выход
          </div>
        </div>
      </Tabs>
      <div className={styles.content}>
        <Content />
      </div>
    </div>
  )
})
UserProfile.displayName = 'UserProfile'
